import type { ReactNode } from 'react';
import { AlertTriangle, Layers, ShieldCheck } from 'lucide-react';
import AccessDenied from '../AccessDenied/AccessDenied';
import { relativeTime } from '../Forum/reportLabels';
import { readApiError } from '../../../sdk/errors/apiError';
import type { AppNotification, NotificationType } from '../../../sdk/@types/Notificacao';
import type { ModerationStats } from '../../../sdk/@types/Forum';

/**
 * O conteúdo do sino: **um** painel com os três blocos que a decisão 1 do
 * design fixa — Notificações, Revisão e, só para perfil administrativo,
 * Moderação.
 *
 * Componente puramente visual: todos os dados e todas as ações chegam por
 * props. Quem lê a API é `useNotifications`, montado pelo `NotificationBell`;
 * é o que permite as stories rodarem sem rede nenhuma.
 *
 * Duas regras de cor que valem para o arquivo inteiro:
 *
 * - **Nenhum hex solto.** Toda cor sai de token (`bg-surface`, `border-edge`,
 *   `text-ink…`, `accent-*` só em tile de ícone). O critério é o da varredura de
 *   `e2e/forum-closeout.spec.ts`: compara o valor **computado** com o valor do
 *   token no tema ativo, então um hex que "parece igual" é pego — e uma cor
 *   apenas *herdada* de fora do painel conta também (ver `text-ink` na raiz).
 * - **A família `danger` não aparece aqui**, nem no estado de erro. Vermelho é
 *   reservado a erro do usuário e ação destrutiva (§7, §54); uma leitura que
 *   falhou e uma negativa de acesso são aviso — família `warn` nos dois casos.
 */

const cx = (...v: Array<string | false | null | undefined>) => v.filter(Boolean).join(' ');

const FOCUS =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-select/60 ' +
  'focus-visible:ring-offset-2 focus-visible:ring-offset-surface';

/** Tile do ícone de um bloco — a única superfície que usa `accent-*` (§8). */
const TILE = 'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-card border';

const plural = (n: number, um: string, muitos: string) => (n === 1 ? um : muitos);

/**
 * Rótulo pt-BR de cada `type` conhecido — o que a AC2 desta tarefa pede como
 * "distinguível no DOM". O back é dono da lista de `type` (ver `Notificacao.ts`)
 * e já está em processo de acrescentar valores; um `type` fora daqui **não** é
 * bug, é o caso normal de um change do back ainda não refletido no front — daí
 * o fallback em vez de um `Record` fechado que forçaria checagem exaustiva.
 */
const NOTIFICATION_TYPE_LABELS: Partial<Record<NotificationType, string>> = {
  report_response: 'Resposta à denúncia',
  report_accepted: 'Denúncia aceita',
  report_rejected: 'Denúncia rejeitada',
  report_resolved: 'Denúncia resolvida',
  comment_approved: 'Comentário aprovado',
  comment_rejected: 'Comentário rejeitado',
  registration_canceled: 'Inscrição cancelada',
};

function notificationTypeLabel(type: string): string {
  return NOTIFICATION_TYPE_LABELS[type as NotificationType] ?? 'Atualização';
}

export interface NotificationPanelProps {
  items: AppNotification[];
  /** Não lidas **da API** (`unread`), nunca `items.length` — a lista pode vir cortada. */
  unread: number;
  /** `dueToday` de `GET /review/stats`. */
  due: number;
  /** Fila do moderador, ou `null` (não-admin, ainda carregando, ou falhou). */
  moderation: ModerationStats | null;
  /**
   * Perfil administrativo. Vem separado de `moderation` de propósito: um admin
   * cuja leitura da fila falhou continua vendo o bloco, sem números.
   */
  isAdmin?: boolean;
  loading?: boolean;
  /** Erro de qualquer fonte; `kind` preenchido vira negativa de acesso. */
  error?: Error | null;
  onMarkRead?: (id: string) => void;
  onMarkAllRead?: () => void;
  /** Navegação SPA. Sem ela os blocos são `<a href>` comuns. */
  onNavigate?: (to: string) => void;
  /** Injetável nos testes e nas stories para fixar o "há 2 h". */
  now?: number;
  className?: string;
  /**
   * `'dropdown'` (padrão) é o balão ancorado ao sino no desktop — largura fixa,
   * moldura própria. `'sheet'` é o corpo usado dentro do drawer mobile do
   * `NotificationBell`: a moldura (borda, sombra, cantos) e a largura já vêm do
   * container que o envolve, então o painel só precisa ocupar 100% dela.
   */
  variant?: 'dropdown' | 'sheet';
}

/* -------------------------------------------------------------------------- */
/* Uma notificação                                                             */
/* -------------------------------------------------------------------------- */

function NotificationRow({
  notification,
  onMarkRead,
  now,
}: {
  notification: AppNotification;
  onMarkRead?: (id: string) => void;
  now?: number;
}) {
  const unread = notification.readAt === null;

  // `now` chega em ms (props/testes fixam o relógio); `relativeTime`
  // (`Forum/reportLabels`) espera um `Date` e devolve '—' para ausente/inválido
  // — tratado aqui como "sem tempo", não como um traço na tela (AC5).
  const timeLabel = relativeTime(notification.createdAt, typeof now === 'number' ? new Date(now) : undefined);
  const showTime = Boolean(timeLabel) && timeLabel !== '—';

  return (
    <li>
      <button
        type="button"
        data-testid="notification-item"
        data-unread={unread ? 'true' : 'false'}
        data-notification-type={notification.type}
        // Continua sendo botão mesmo já lida: o clique é possível e, por
        // contrato, não dispara requisição nenhuma (o hook devolve cedo).
        onClick={() => onMarkRead?.(notification.id)}
        aria-label={`${notification.title}${unread ? ' — não lida' : ' — lida'}`}
        className={cx(
          'flex w-full gap-3 px-4 py-3 text-left transition-colors',
          'hover:bg-surface-hover',
          unread ? 'bg-surface-muted' : 'bg-surface',
          FOCUS,
        )}
      >
        {/* O ponto é o marcador de não lida; lida reserva o mesmo espaço para
            as duas linhas não dançarem na lista. */}
        <span
          aria-hidden
          data-testid={unread ? 'notification-unread-dot' : 'notification-read-dot'}
          className={cx(
            'mt-1.5 h-2 w-2 shrink-0 rounded-full',
            unread ? 'bg-brand' : 'bg-transparent',
          )}
        />

        <span className="flex min-w-0 flex-1 flex-col gap-1">
          {/* Rótulo do `type` — distinguível por tipo (AC2) e nunca ausente:
              um `type` fora dos sete conhecidos cai no rótulo genérico em vez
              de sumir ou quebrar o painel (AC1). */}
          <span
            data-testid="notification-type-label"
            className="w-fit text-[10px] font-semibold uppercase tracking-wide text-ink-muted"
          >
            {notificationTypeLabel(notification.type)}
          </span>

          <span className="flex items-baseline justify-between gap-2">
            <span
              className={cx(
                // `line-clamp-2`, e não `truncate`: o título é a frase que diz
                // o que aconteceu, e numa coluna de 360px o corte em UMA linha
                // comia o fim de quase todo título real ("Seu comentário na
                // questão 1234 foi…"). Duas linhas cabem sem mexer na altura
                // típica da linha e ainda limitam o caso patológico — o que
                // some de verdade continua sendo contido, não ilimitado.
                'min-w-0 break-words text-sm [overflow-wrap:anywhere] line-clamp-2',
                unread ? 'font-semibold text-ink' : 'font-medium text-ink-secondary',
              )}
            >
              {notification.title}
            </span>
            {showTime && (
              <span data-testid="notification-time" className="shrink-0 text-[11px] text-ink-muted">
                {timeLabel}
              </span>
            )}
          </span>

          {/* Corpo vazio não deixa bloco vazio na tela (AC3); corpo muito
              longo é contido em altura e nunca estoura a largura do painel —
              `break-words` para uma sequência sem espaço, `line-clamp-3` para
              o texto normal. */}
          {notification.body && (
            <span
              data-testid="notification-body"
              className="line-clamp-3 break-words text-xs leading-relaxed text-ink-secondary"
            >
              {notification.body}
            </span>
          )}

          {/* Referência da questão como **texto**, nunca link: não existe URL
              por questão para o aluno — `/carrossel` não lê query param e
              `/questao` é o editor admin (decisão 8 do design). Sem
              `questionId` a linha não existe, em vez de existir vazia. */}
          {notification.questionId && (
            <span
              data-testid="notification-question-ref"
              className="mt-0.5 inline-flex w-fit items-center rounded-chip border border-edge bg-surface-muted px-2 py-0.5 text-[11px] text-ink-muted"
            >
              Questão {notification.questionId}
            </span>
          )}
        </span>
      </button>
    </li>
  );
}

/* -------------------------------------------------------------------------- */
/* Um bloco de contador (Revisão, Moderação)                                   */
/* -------------------------------------------------------------------------- */

function CounterBlock({
  testId,
  to,
  icon,
  tone,
  title,
  lines,
  onNavigate,
}: {
  testId: string;
  to: string;
  icon: ReactNode;
  /** Classe do tile — `accent-*`, a identidade da seção. */
  tone: string;
  title: string;
  lines: ReactNode;
  onNavigate?: (to: string) => void;
}) {
  return (
    <a
      href={to}
      data-testid={testId}
      onClick={
        onNavigate
          ? (event) => {
              event.preventDefault();
              onNavigate(to);
            }
          : undefined
      }
      className={cx(
        // `shrink-0`: num painel limitado pela altura da janela, estes blocos
        // são a última coisa que pode ceder. Eles são um resumo de uma linha —
        // encolhê-los não revela nada, só os corta. Quem cede é a lista, que
        // tem rolagem própria e onde rolar não perde informação nenhuma.
        'flex shrink-0 items-center gap-3 border-t border-edge bg-surface px-4 py-3',
        'transition-colors hover:bg-surface-hover',
        FOCUS,
      )}
    >
      <span className={cx(TILE, tone)} aria-hidden>
        {icon}
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="text-sm font-semibold text-ink">{title}</span>
        <span className="text-xs text-ink-secondary">{lines}</span>
      </span>
    </a>
  );
}

/* -------------------------------------------------------------------------- */
/* O painel                                                                    */
/* -------------------------------------------------------------------------- */

export default function NotificationPanel({
  items,
  unread,
  due,
  moderation,
  isAdmin = false,
  loading = false,
  error = null,
  onMarkRead,
  onMarkAllRead,
  onNavigate,
  now,
  className,
  variant = 'dropdown',
}: NotificationPanelProps) {
  const denial = error ? readApiError(error) : null;

  return (
    <div
      data-testid="notification-panel"
      // No drawer mobile o papel de diálogo e o rótulo já estão no wrapper do
      // `NotificationBell` que envolve este painel — duplicar aqui criaria
      // dois `role="dialog"` aninhados para o mesmo conteúdo.
      role={variant === 'sheet' ? undefined : 'dialog'}
      aria-label={variant === 'sheet' ? undefined : 'Notificações e pendências'}
      className={cx(
        'flex flex-col overflow-hidden',
        // No drawer mobile a moldura e a largura já são do container que
        // envolve o painel (`NotificationBell`); aqui ele só ocupa 100%.
        variant === 'sheet'
          ? 'w-full'
          : [
              'w-[360px] max-w-[calc(100vw-1.5rem)] rounded-panel border border-edge shadow-panel',
              /*
                ⚠️ O teto de altura é o que faltava, e é por isso que "com
                muita notificação alguma informação some".
            
                O balão do desktop era `absolute` sem limite nenhum, e a raiz
                aqui é `overflow-hidden`: o painel crescia para baixo e, passado
                o fim da janela, o que sobrava — justamente os blocos "Revisão"
                e "Moderação", que ficam no pé — não tinha como ser alcançado.
                Não havia rolagem para chegar até ele. O drawer mobile já era
                limitado (`max-h-[calc(100dvh-4rem)]` em NotificationBell); só o
                desktop não era.
            
                5,5rem = a altura do header (4rem) mais o deslocamento do balão
                e uma folga igual embaixo, para ele não encostar na borda.
              */
              'max-h-[calc(100vh-5.5rem)]',
            ].join(' '),
        // `text-ink` e `leading-normal` na raiz não são decorativos: o painel é
        // filho do header, e herda dele duas coisas que não quer.
        //
        // 1. `text-gray-800 dark:text-white`, hardcoded no wrapper do Header —
        //    sem ancorar a cor herdada num token, todo elemento que não declara
        //    a sua (li, ul, section, e o `outline-color`, que segue
        //    `currentColor`) cai em `rgb(31,41,55)` no claro, fora da paleta.
        // 2. `line-height: 64px`, do `.ant-layout-header` — a altura da barra,
        //    herdada. Quem não traz utilitário de leading (o corpo, o chip da
        //    questão) ganhava 64px de altura de linha e inchava a linha da
        //    notificação para 188px, contra os ~76px que o `py-3` desenha.
        'bg-surface text-ink leading-normal',
        className,
      )}
    >
      {/* ---------------------------------------------------- Notificações */}
      <section
        data-testid="notification-block-notificacoes"
        aria-label="Notificações"
        // `min-h-0` é o que permite encolher abaixo do conteúdo: sem ele um
        // filho de flex se recusa a ficar menor que a lista inteira, o teto de
        // altura da raiz não tem efeito e os blocos de baixo voltam a ser
        // empurrados para fora.
        className={cx('flex min-h-0 flex-col', variant === 'sheet' ? '' : 'flex-1')}
      >
        <header className="flex shrink-0 items-center justify-between gap-2 bg-surface px-4 py-3">
          <h2 className="m-0 text-sm font-semibold text-ink">Notificações</h2>
          {unread > 0 && (
            <button
              type="button"
              onClick={() => onMarkAllRead?.()}
              className={cx(
                'rounded-input px-2 py-1 text-xs font-semibold text-ink-secondary',
                'transition-colors hover:bg-surface-hover hover:text-ink',
                FOCUS,
              )}
            >
              Marcar todas como lidas
            </button>
          )}
        </header>

        <div
          className={cx(
            'overflow-y-auto border-t border-edge',
            /*
              No desktop a lista toma a altura que sobrar depois do cabeçalho e
              dos dois blocos do pé, em vez dos 300px fixos de antes — numa
              janela alta isso mostra bem mais notificações sem cortar nada, e
              numa baixa ela encolhe em vez de empurrar o resto para fora.

              No drawer mobile quem rola é o container do `NotificationBell`,
              que já envolve o painel inteiro. Limitar a lista aqui também
              criaria rolagem dentro de rolagem: o dedo pararia na lista em vez
              de levar o drawer.
            */
            variant === 'sheet' ? '' : 'min-h-0 flex-1',
          )}
        >
          {loading && items.length === 0 && !error && (
            <p role="status" className="m-0 px-4 py-6 text-center text-xs text-ink-muted">
              Carregando notificações…
            </p>
          )}

          {/* Erro: negativa de acesso vira `AccessDenied` (família `warn`);
              qualquer outra falha vira um aviso, também em `warn` — o painel
              inteiro fica fora da família `danger`. */}
          {error &&
            (denial?.kind ? (
              <div className="px-4 py-4">
                <AccessDenied
                  kind={denial.kind}
                  variant="panel"
                  detail={denial.raw}
                  onNavigate={onNavigate}
                />
              </div>
            ) : (
              <div
                role="status"
                data-testid="notification-error"
                className="flex items-start gap-3 px-4 py-5"
              >
                <span
                  className={cx(TILE, 'border-warn/40 bg-warn/[0.12] text-warn-ink')}
                  aria-hidden
                >
                  <AlertTriangle size={18} />
                </span>
                <span className="flex flex-col gap-1">
                  {/* Com lista na tela a falha é de uma ação (marcar como
                      lida), não da carga — dizer "não foi possível carregar"
                      por cima de notificações visíveis seria mentira. */}
                  <span className="text-sm font-semibold text-ink">
                    {items.length > 0
                      ? 'Não foi possível concluir a ação'
                      : 'Não foi possível carregar suas notificações'}
                  </span>
                  <span className="text-xs leading-relaxed text-ink-secondary">
                    {items.length > 0
                      ? 'Nada foi alterado. O resto do produto continua normal.'
                      : 'O resto do produto continua normal. Tente de novo mais tarde.'}
                  </span>
                </span>
              </div>
            ))}

          {!error && !loading && items.length === 0 && (
            <div
              data-testid="notification-empty"
              className="flex flex-col items-center gap-1 px-6 py-8 text-center"
            >
              <p className="m-0 text-sm font-semibold text-ink">Nenhuma notificação</p>
              <p className="m-0 text-xs leading-relaxed text-ink-secondary">
                Avisamos aqui quando houver resposta a uma denúncia ou decisão sobre um
                comentário seu.
              </p>
            </div>
          )}

          {/* A lista sobrevive ao erro **de propósito**. O `error` do hook é um
              só para as três fontes, então uma falha de `POST
              /notifications/:id/read` chega aqui igual a uma falha do `GET`. Se
              o erro escondesse a lista, a notificação que o hook acabou de
              reverter para não lida sumiria da tela no exato momento em que o
              usuário precisa vê-la voltar — a contagem reverteria e a linha,
              não. Com o `GET` falhando na primeira carga `items` está vazia e
              só o aviso aparece, que é o outro caso. */}
          {items.length > 0 && (
            <ul className="m-0 flex list-none flex-col divide-y divide-edge p-0">
              {items.map((notification) => (
                <NotificationRow
                  key={notification.id}
                  notification={notification}
                  onMarkRead={onMarkRead}
                  now={now}
                />
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* --------------------------------------------------------- Revisão */}
      <CounterBlock
        testId="notification-block-revisao"
        to="/revisao"
        icon={<Layers size={18} />}
        tone="border-accent-purple/40 bg-accent-purple/[0.14] text-accent-purple-ink"
        title="Revisão"
        lines={
          due > 0
            ? `${due} ${plural(due, 'cartão vencendo', 'cartões vencendo')} hoje`
            : 'Revisão em dia'
        }
        onNavigate={onNavigate}
      />

      {/* ------------------------------------------------------- Moderação */}
      {/* Só para `administrador`/`master`: o gate da chamada mora no hook, e o
          gate visual aqui — um aluno nunca vê o bloco nem a rota. */}
      {isAdmin && (
        <CounterBlock
          testId="notification-block-moderacao"
          to="/admin/moderacao"
          icon={<ShieldCheck size={18} />}
          tone="border-accent-cyan/40 bg-accent-cyan/[0.14] text-accent-cyan-ink"
          title="Moderação"
          lines={
            moderation ? (
              <>
                <span data-testid="moderacao-pendentes">
                  {moderation.pendingComments}{' '}
                  {plural(moderation.pendingComments, 'comentário pendente', 'comentários pendentes')}
                </span>
                {' · '}
                <span data-testid="moderacao-denuncias">
                  {moderation.openReports}{' '}
                  {plural(moderation.openReports, 'denúncia aberta', 'denúncias abertas')}
                </span>
              </>
            ) : (
              'Fila de moderação'
            )
          }
          onNavigate={onNavigate}
        />
      )}
    </div>
  );
}
